<script setup lang="ts">
import { useRoute, createError } from '#app';
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useSpotlight } from '@/composables/useSpotlight';
import {
  applySpotlightPostDetailLocale,
  applySpotlightPostsListLocale,
  faqAnswerHtml,
} from '@/lib/spotlight-locale';
import { i18n, t } from '@/i18n';
import SpotlightPortableBody from '@/components/spotlight/SpotlightPortableBody.vue';
import { provideSeoDetailLanguagePaths } from '@/composables/useSeoDetailLanguageNavigation';
import { generateSpotlightSlug as generateSlug } from '@/lib/spotlightAnchor';
import AppNav from '@/components/AppNav.vue';
import AppFooter from '@/components/AppFooter.vue';
import FeaturesRoles from '@/components/features/FeaturesRoles.vue';
import {
  absoluteLocalizedUrl,
  buildDetailLanguagePaths,
  buildDetailHreflangLinks,
  hreflangFor,
  localizedPath,
  normalizeTranslationAlternates,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

interface ContentParagraph {
  type: 'paragraph';
  text: string;
  links?: Array<{ text: string; url: string; target?: string }>;
  bold?: string[];
  italic?: string[];
}

interface ContentHeading {
  type: 'heading';
  level: 1 | 2 | 3 | 4 | 5 | 6;
  text: string;
}

interface ContentList {
  type: 'list';
  listType: 'ordered' | 'unordered';
  items: Array<{
    text?: string;
    html?: string;
    bold?: string[];
    links?: Array<{ text: string; url: string; target?: string }>;
  }>;
}

interface ContentImage {
  type: 'image';
  src: string;
  alt: string;
  /** Rich caption HTML (from markdown `*caption*` after an image); styled like YouTube embed captions */
  captionHtml?: string;
}

interface ContentYouTube {
  type: 'youtube';
  url: string;
  title?: string;
  caption?: string;
}

interface ContentSection {
  type: 'section';
  title: string;
  level: 1 | 2 | 3 | 4 | 5 | 6;
  content: ContentBlock[];
}

type ContentBlock =
  | ContentParagraph
  | ContentHeading
  | ContentList
  | ContentImage
  | ContentYouTube
  | ContentSection
  | { type: 'paragraphHtml'; html: string };

const route = useRoute();
const pageLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));

/** Must be reactive: Vue Router reuses this page component when switching between slugs. */
const slug = computed(() => {
  const raw = route.params.slug;
  const s = Array.isArray(raw) ? raw[0] : raw;
  return s != null && s !== '' ? String(s) : '';
});

const { fetchSpotlightPostBySlug, fetchRecommendedSpotlight } = useSpotlight();

interface BlogCategory {
  title: string;
}

interface RelatedPost {
  _id: string;
  slug: string;
  language?: string;
  translationKey?: string;
  alternates?: Array<{ language?: string; slug?: string }>;
  title: string;
  excerpt?: string | unknown[];
  image?: string;
  publishedAt?: string;
}

interface YouTubeLink {
  url: string;
  title?: string;
}

interface BlogPostData {
  _id: string;
  slug: string;
  title: string;
  excerpt?: any[]; // Portable Text array
  /** When true, excerpt duplicates the opening body paragraph — hide the excerpt block. */
  excerptDerivedFromBody?: boolean;
  body?: any[];
  bodyContent?: ContentBlock[];
  /** Sanity Portable Text (preferred when present) */
  bodyPortableText?: unknown[] | null;
  image?: string;
  imageAlt?: string;
  publishedAt?: string;
  _updatedAt?: string;
  categories?: BlogCategory[];
  tags?: string[];
  readTime?: number;
  faq?: Array<{ question: string; answer: string }>;
  youtubeLinks?: YouTubeLink[];
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  author?: { name: string; avatarUrl?: string };
  relatedPosts?: RelatedPost[];
}

const { data: articleData, error: articleError } = await useAsyncData(
  () => `spotlight-article:${pageLocale.value}:${slug.value}`,
  async () => {
    if (!slug.value) return null;
    try {
      const data = await fetchSpotlightPostBySlug(slug.value, pageLocale.value);
      if (!data) {
        throw createError({ statusCode: 404, statusMessage: 'Article not found' });
      }
      return pageLocale.value === 'en' ? await applySpotlightPostDetailLocale(data) : data;
    } catch (error: unknown) {
      const err = error as { statusCode?: number };
      if (err.statusCode === 404) {
        throw error;
      }
      console.error('Error fetching spotlight article:', error);
      return null;
    }
  },
  {
    server: true,
    lazy: false,
    default: () => null,
    watch: [slug, pageLocale],
  }
);

if (articleError.value) {
  throw articleError.value;
}

// Static Markdown → same article shape as Blog
const article = computed(() => {
  if (!articleData.value) return null;

  const data = articleData.value;

  const excerptText =
    typeof data.excerpt === 'string' ? data.excerpt : Array.isArray(data.excerpt) ? '' : '';

  return {
    _id: data._id || '',
    slug: data.slug || '',
    language: data.language || pageLocale.value,
    translationKey: data.translationKey,
    alternates: Array.isArray(data.alternates) ? data.alternates : [],
    title: data.title || '',
    excerpt: data.excerpt,
    excerptDerivedFromBody: Boolean(data.excerptDerivedFromBody),
    body: data.body || [],
    bodyContent: (data.bodyContent || []) as ContentBlock[],
    bodyPortableText: Array.isArray(data.bodyPortableText) ? data.bodyPortableText : null,
    image: data.image || null,
    imageAlt: data.imageAlt || data.title || '',
    publishedAt: data.publishedAt || '',
    _updatedAt: data._updatedAt || data.publishedAt || '',
    categories: data.categories || [],
    tags: data.tags || [],
    readTime: data.readTime || 0,
    faq: data.faq || [],
    youtubeLinks: data.youtubeLinks || [],
    seoTitle: data.seoTitle || `${data.title} | Veeso Spotlight`,
    seoDescription:
      data.seoDescription ||
      excerptText ||
      t('landingPages.spotlight.detail.seo_description_fallback'),
    keywords: data.keywords || [],
    canonicalUrl: data.canonicalUrl || `https://veeso.ai/spotlight/${data.slug}`,
    ogImage: data.ogImage || data.image || null,
    ogImageAlt: data.ogImageAlt,
    author: data.author as { name: string; avatarUrl?: string } | undefined,
    relatedPosts: (Array.isArray(data.relatedPosts) ? data.relatedPosts : []).map((rp) => ({
      _id: rp._id || '',
      slug: rp.slug || '',
      title: rp.title || '',
      excerpt: rp.excerpt,
      image: rp.image || null,
      publishedAt: rp.publishedAt || '',
    })),
  } as unknown as BlogPostData;
});

const canonicalUrl = computed(() => {
  return absoluteLocalizedUrl(pageLocale.value, `/spotlight/${encodeURIComponent(slug.value)}`);
});

const translationAlternates = computed(() =>
  normalizeTranslationAlternates(
    (articleData.value as { alternates?: unknown } | null)?.alternates,
    slug.value ? { language: pageLocale.value, slug: slug.value } : undefined
  )
);

const alternateHeadLinks = computed(() =>
  buildDetailHreflangLinks('spotlight', translationAlternates.value)
);
const detailLanguagePaths = computed(() =>
  buildDetailLanguagePaths('spotlight', translationAlternates.value)
);
provideSeoDetailLanguagePaths(detailLanguagePaths);

const { data: recommendedPostsDataRaw } = await useAsyncData(
  () => `spotlight-recommended:${pageLocale.value}:${slug.value}`,
  async () => {
    if (!slug.value) return [];
    try {
      return await fetchRecommendedSpotlight(slug.value, 4, pageLocale.value);
    } catch (error) {
      console.error('Error fetching recommended spotlight posts:', error);
      return [];
    }
  },
  {
    server: true,
    lazy: false,
    default: () => [],
    watch: [slug, pageLocale],
  }
);

// Transform data using computed (lazy evaluation)
const recommendedPosts = computed(() => {
  void i18n.locale;
  if (!recommendedPostsDataRaw.value || !Array.isArray(recommendedPostsDataRaw.value)) {
    return [];
  }
  const posts = recommendedPostsDataRaw.value.map((p) => ({
    _id: p.id,
    slug: p.slug,
    title: p.title,
    excerpt: '',
    category: p.category,
    image: p.image,
    categories: [{ title: p.category }],
  }));
  const localizedPosts = pageLocale.value === 'en' ? applySpotlightPostsListLocale(posts) : posts;
  return localizedPosts.map((p) => ({
    id: p._id,
    slug: p.slug,
    title: p.title,
    category:
      p.category ||
      p.categories?.[0]?.title ||
      t('landingPages.spotlight.detail.breadcrumb.spotlight'),
    image: p.image || '',
  }));
});

// 日期格式化函数
function formatDate(dateString: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const locale =
    {
      en: 'en-US',
      ja: 'ja-JP',
      ko: 'ko-KR',
      es: 'es-ES',
    }[pageLocale.value] || 'en-US';
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Plain-text excerpt paragraphs; strips Markdown HR-only blocks (`---`). */
function parseExcerptParagraphs(raw: string): string[] {
  return raw
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && !/^-{3,}\s*$/.test(p));
}

const avatarFiles = [
  'bird.png',
  'cat.png',
  'chihuahua.png',
  'dog.png',
  'face-of--maine-coon.png',
  'face-of--ragdoll.png',
  'face-of-british-shorthair.png',
  'pomeranian.png',
  'shih-tzu.png',
  'spix-s-macaw.png',
  'toy-poodle.png',
];

function getRandomAvatar(): string {
  const randomIndex = Math.floor(Math.random() * avatarFiles.length);
  return `/avatar/${avatarFiles[randomIndex]}`;
}

const authorName = computed(
  () => article.value?.author?.name || t('landingPages.articleDetail.meta.author')
);

const authorAvatar = computed(() => {
  if (article.value?.author?.avatarUrl) return article.value.author.avatarUrl;
  if (!article.value?.slug) return getRandomAvatar();
  let hash = 0;
  for (let i = 0; i < article.value.slug.length; i++) {
    hash = article.value.slug.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % avatarFiles.length;
  return `/avatar/${avatarFiles[index]}`;
});

const shareLinks = computed(() => {
  if (!article.value?.title || !canonicalUrl.value) return null;
  return {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalUrl.value)}&text=${encodeURIComponent(article.value.title)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl.value)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl.value)}`,
    discord: 'https://discord.gg/twbZEHkuWx',
  };
});

function handleShare(type: 'twitter' | 'linkedin' | 'discord' | 'facebook') {
  if (!shareLinks.value) return;

  const url = shareLinks.value[type];
  if (!url) return;

  if (type === 'twitter' || type === 'linkedin' || type === 'facebook') {
    const width = 600;
    const height = 400;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      url,
      'share',
      `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0,location=0,status=0,scrollbars=1,resizable=1`
    );
    return;
  }

  if (type === 'discord') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function ptBlockPlainText(block: { children?: Array<{ text?: string }> }): string {
  if (!block?.children) return '';
  return block.children.map((c) => (c?.text ? String(c.text) : '')).join('');
}

// 从 YouTube URL 提取 video ID
function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // 匹配各种 YouTube URL 格式
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

interface FaqItem {
  question: string;
  answer: string;
  answerHtml: string;
}

function isFaqSectionHeading(text: string): boolean {
  const normalized = generateSlug(text).toLowerCase();
  return (
    normalized === 'faq' ||
    normalized === 'frequently-asked-questions' ||
    normalized === 'frequently-asked-questions-faq'
  );
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function stripHtml(text: string): string {
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function inlinePartsToHtml(
  parts: Array<{ type: string; content: string; url?: string; target?: string }>
): string {
  return parts
    .map((part) => {
      if (part.type === 'bold') {
        return `<strong>${escapeHtml(part.content)}</strong>`;
      }
      if (part.type === 'link' && part.url) {
        const target = part.target || '_blank';
        return `<a href="${escapeHtml(part.url)}" target="${escapeHtml(target)}" rel="nofollow noreferrer">${escapeHtml(part.content)}</a>`;
      }
      return escapeHtml(part.content);
    })
    .join('');
}

function contentBlockToFaqHtml(block: ContentBlock): string {
  if (block.type === 'paragraphHtml') {
    return `<div class="faq-answer-block">${block.html}</div>`;
  }

  if (block.type === 'paragraph') {
    return `<p>${inlinePartsToHtml(renderParagraph(block))}</p>`;
  }

  if (block.type === 'list') {
    const tag = block.listType === 'ordered' ? 'ol' : 'ul';
    const items = block.items
      .map((item) => {
        const inner = item.html ? item.html : inlinePartsToHtml(renderListItem(item));
        return `<li>${inner}</li>`;
      })
      .join('');
    return `<${tag}>${items}</${tag}>`;
  }

  return '';
}

const markdownFaqExtraction = computed(() => {
  const blocks = ((article.value?.bodyContent || []) as ContentBlock[]).slice();
  const faqStartIndex = blocks.findIndex(
    (block) => block.type === 'heading' && block.level === 2 && isFaqSectionHeading(block.text)
  );

  if (faqStartIndex === -1) {
    return {
      title: t('landing_home.faq.title'),
      items: [] as FaqItem[],
      bodyBlocks: blocks,
    };
  }

  const faqHeading = blocks[faqStartIndex] as ContentHeading;
  const faqBlocks = blocks.slice(faqStartIndex + 1);
  const items: FaqItem[] = [];
  let currentQuestion = '';
  let currentAnswerBlocks: ContentBlock[] = [];

  const pushCurrentItem = () => {
    if (!currentQuestion) return;
    const answerHtml = currentAnswerBlocks.map(contentBlockToFaqHtml).filter(Boolean).join('');
    items.push({
      question: currentQuestion,
      answer: stripHtml(answerHtml),
      answerHtml,
    });
  };

  for (const block of faqBlocks) {
    if (block.type === 'heading' && block.level === 3) {
      pushCurrentItem();
      currentQuestion = block.text;
      currentAnswerBlocks = [];
      continue;
    }

    currentAnswerBlocks.push(block);
  }

  pushCurrentItem();

  return {
    title: faqHeading.text,
    items,
    bodyBlocks: blocks.slice(0, faqStartIndex),
  };
});

const articleBodyContent = computed<ContentBlock[]>(() => markdownFaqExtraction.value.bodyBlocks);

const faqSectionTitle = computed(
  () => markdownFaqExtraction.value.title || t('landingPages.articleDetail.faq.title')
);

const faqItems = computed<FaqItem[]>(() => {
  const raw = article.value?.faq;
  if (raw && Array.isArray(raw) && raw.length > 0) {
    return raw.map((item: { question?: string; answer?: string }) => {
      const answer = item.answer || '';
      return {
        question: item.question || '',
        answer: stripHtml(faqAnswerHtml(answer)) || answer,
        answerHtml: faqAnswerHtml(answer),
      };
    });
  }

  return markdownFaqExtraction.value.items;
});

// 提取目录项（只保留 H2 标题，并添加 FAQ）
// 注意：只显示 H2 标题，H3-H6 作为子标题不显示在目录中
const tableOfContents = computed(() => {
  const toc: Array<{ text: string; id: string }> = [];

  const pt = article.value?.bodyPortableText;
  if (pt && Array.isArray(pt)) {
    for (const raw of pt) {
      const block = raw as { _type?: string; style?: string; children?: Array<{ text?: string }> };
      if (block._type === 'block' && block.style === 'h2') {
        const text = ptBlockPlainText(block);
        if (text) {
          toc.push({ text, id: generateSlug(text) });
        }
      }
    }
  } else if (articleBodyContent.value.length) {
    articleBodyContent.value.forEach((block) => {
      if (block.type === 'heading' && block.level === 2) {
        const id = generateSlug(block.text);
        toc.push({
          text: block.text,
          id,
        });
      }
    });
  }

  // 如果有 FAQ，添加到目录末尾
  if (faqItems.value.length > 0) {
    toc.push({
      text: t('landingPages.articleDetail.faq.title'),
      id: 'faq-section',
    });
  }

  return toc;
});

// 当前激活的目录项
const activeTocId = ref<string>('');
// 滚动停止检测的定时器
let scrollStopTimer: ReturnType<typeof setTimeout> | null = null;
// 滚动监听器是否已附加
let scrollListenerAttached = false;
// 侧边栏的垂直偏移量（用于向上滑动）
const sidebarOffset = ref(0);
// FAQ 展开状态
const expandedFaqItems = ref<Set<number>>(new Set());

// 跳转到锚点
function scrollToAnchor(id: string) {
  const element = document.getElementById(id);
  if (element) {
    // 立即设置激活项和高亮
    activeTocId.value = id;

    // 完全移除滚动监听器，禁止任何跟随
    if (scrollListenerAttached && typeof window !== 'undefined') {
      window.removeEventListener('scroll', handleScroll);
      scrollListenerAttached = false;
    }

    const offset = 100; // 偏移量，避免被固定导航栏遮挡
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });

    // 清除之前的定时器
    if (scrollStopTimer) {
      clearTimeout(scrollStopTimer);
    }

    // 平滑滚动通常需要 500-1500ms，设置足够长的延迟确保滚动完成后再恢复监听
    scrollStopTimer = setTimeout(() => {
      // 滚动完成后，恢复滚动监听器
      if (typeof window !== 'undefined' && !scrollListenerAttached) {
        window.addEventListener('scroll', handleScroll, { passive: true });
        scrollListenerAttached = true;
        // 更新一次高亮状态
        updateActiveToc();
      }
    }, 2000); // 2 秒，确保滚动完全完成
  }
}

// 检测当前视口中的章节
function updateActiveToc() {
  if (tableOfContents.value.length === 0) return;
  if (typeof window === 'undefined') return;

  const viewportHalf = window.innerHeight / 2; // 屏幕高度的一半
  const headings = tableOfContents.value
    .map((item) => {
      const element = document.getElementById(item.id);
      if (!element) return null;
      const rect = element.getBoundingClientRect();
      return {
        id: item.id,
        element,
        top: rect.top, // 相对于视口的位置
      };
    })
    .filter((h): h is { id: string; element: HTMLElement; top: number } => h !== null);

  if (headings.length === 0) return;

  // 找到第一个超过屏幕高度一半的标题
  let currentId = headings[0]?.id || '';
  for (let i = 0; i < headings.length; i++) {
    const heading = headings[i];
    if (!heading) continue;
    // 当标题超过屏幕高度一半时，切换到下一个标题
    if (heading.top <= viewportHalf) {
      currentId = heading.id;
    } else {
      break;
    }
  }

  activeTocId.value = currentId;
}

// 检测侧边栏位置并计算偏移量（当触碰到分割线时向上滑动，进入 CTA 时完全隐藏）
function checkShouldHideSidebars() {
  if (typeof window === 'undefined') return;

  const viewportHeight = window.innerHeight;
  const viewportCenter = viewportHeight / 2;

  // 进入 CTA（FeaturesRoles）区域时立刻完全隐藏侧边栏
  const ctaSection = document.querySelector('[data-section="cta"]');
  if (ctaSection) {
    const ctaTop = ctaSection.getBoundingClientRect().top;
    if (ctaTop <= viewportHeight * 0.8) {
      sidebarOffset.value = viewportHeight;
      return;
    }
  }

  // 检测分割线位置（Recommended Posts 前面的分割线，文章 ≥ 4 篇时才渲染）
  const divider = document.querySelector('[data-divider="recommended"]');
  if (!divider) {
    sidebarOffset.value = 0;
    return;
  }

  const dividerTop = divider.getBoundingClientRect().top;
  const sidebarBottom = viewportCenter + 200;
  const minDistance = 60;

  if (sidebarBottom > dividerTop - minDistance) {
    const offset = sidebarBottom - (dividerTop - minDistance);
    sidebarOffset.value = Math.max(0, offset);
  } else {
    sidebarOffset.value = 0;
  }
}

// FAQ 切换函数
function toggleFaqItem(index: number) {
  if (expandedFaqItems.value.has(index)) {
    expandedFaqItems.value.delete(index);
  } else {
    expandedFaqItems.value.clear();
    expandedFaqItems.value.add(index);
  }
}

function isFaqExpanded(index: number) {
  return expandedFaqItems.value.has(index);
}

// 滚动监听
function handleScroll() {
  updateActiveToc();
  checkShouldHideSidebars();
}

onMounted(() => {
  if (import.meta.client) {
    // 初始检测
    updateActiveToc();
    checkShouldHideSidebars();
    // 添加滚动监听
    window.addEventListener('scroll', handleScroll, { passive: true });
    scrollListenerAttached = true;
  }
});

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', handleScroll);
    // 清理滚动停止检测定时器
    if (scrollStopTimer) {
      clearTimeout(scrollStopTimer);
      scrollStopTimer = null;
    }
  }
});

// 渲染结构化内容的辅助函数（支持加粗和链接）
function renderParagraph(block: ContentParagraph) {
  const text = block.text;
  const parts: any[] = [];
  const currentIndex = 0;
  const markers: Array<{
    index: number;
    type: string;
    content: string;
    url?: string;
    target?: string;
  }> = [];

  if (block.bold) {
    block.bold.forEach((boldText) => {
      const index = text.indexOf(boldText, currentIndex);
      if (index !== -1) {
        markers.push({ index, type: 'bold', content: boldText });
      }
    });
  }

  if (block.links) {
    block.links.forEach((link) => {
      if (!link.text || !link.url) {
        return;
      }
      const index = text.indexOf(link.text, currentIndex);
      if (index !== -1) {
        markers.push({
          index,
          type: 'link',
          content: link.text,
          url: link.url,
          target: link.target,
        });
      }
    });
  }

  markers.sort((a, b) => a.index - b.index);

  let lastIndex = 0;
  markers.forEach((marker) => {
    if (marker.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, marker.index) });
    }
    if (marker.type === 'bold') {
      parts.push({ type: 'bold', content: marker.content });
    } else if (marker.type === 'link') {
      parts.push({ type: 'link', content: marker.content, url: marker.url, target: marker.target });
    }
    lastIndex = marker.index + marker.content.length;
  });

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
}

function renderListItem(item: ContentList['items'][0]) {
  // HTML items are rendered with v-html directly in the template
  if (item.html) return [];
  const text = item.text ?? '';
  const parts: any[] = [];
  const currentIndex = 0;
  const markers: Array<{
    index: number;
    type: string;
    content: string;
    url?: string;
    target?: string;
  }> = [];

  if (item.bold) {
    item.bold.forEach((boldText) => {
      const index = text.indexOf(boldText, currentIndex);
      if (index !== -1) {
        markers.push({ index, type: 'bold', content: boldText });
      }
    });
  }

  if (item.links) {
    item.links.forEach((link) => {
      if (!link.text || !link.url) {
        return;
      }
      const index = text.indexOf(link.text, currentIndex);
      if (index !== -1) {
        markers.push({
          index,
          type: 'link',
          content: link.text,
          url: link.url,
          target: link.target,
        });
      }
    });
  }

  markers.sort((a, b) => a.index - b.index);

  let lastIndex = 0;
  markers.forEach((marker) => {
    if (marker.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, marker.index) });
    }
    if (marker.type === 'bold') {
      parts.push({ type: 'bold', content: marker.content });
    } else if (marker.type === 'link') {
      parts.push({ type: 'link', content: marker.content, url: marker.url, target: marker.target });
    }
    lastIndex = marker.index + marker.content.length;
  });

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
}

// SEO Meta 配置
if (article.value) {
  const art = article.value;
  // SEO 字符长度验证和截断
  const seoTitle = art.seoTitle || art.title || '';
  const seoTitleFinal = seoTitle.length > 60 ? seoTitle.substring(0, 57) + '...' : seoTitle;

  // Extract plain text from excerpt if it's portable text array
  const excerptPlainText = Array.isArray(art.excerpt)
    ? art.excerpt
        .map((block: any) => {
          if (block._type === 'block' && block.children) {
            return block.children
              .map((child: any) => {
                if (typeof child === 'string') return child;
                if (child.text) return child.text;
                return '';
              })
              .join('');
          }
          return '';
        })
        .join(' ')
        .trim()
    : art.excerpt || '';

  const seoDescriptionRaw = art.seoDescription || excerptPlainText || '';
  const seoDescriptionFinal =
    seoDescriptionRaw.length > 160
      ? seoDescriptionRaw.substring(0, 157) + '...'
      : seoDescriptionRaw;

  const keywordsStr = Array.isArray(art.keywords) ? art.keywords.join(', ') : art.keywords || '';
  // Keywords 总长度限制 100 字符
  const keywordsStrFinal =
    keywordsStr.length > 100 ? keywordsStr.substring(0, 97) + '...' : keywordsStr;

  // 确保图片 URL 完整（添加协议和域名）
  const requestURL = useRequestURL();
  const defaultOgImage = `${requestURL.origin}/Veeso_og.jpg`;
  const ogImageUrl = art.ogImage
    ? art.ogImage.startsWith('http')
      ? art.ogImage
      : `${requestURL.origin}${art.ogImage}`
    : art.image
      ? art.image.startsWith('http')
        ? art.image
        : `${requestURL.origin}${art.image}`
      : defaultOgImage;

  const metaTags = [
    // Basic SEO
    { name: 'description', content: seoDescriptionFinal },
    { name: 'keywords', content: keywordsStrFinal },
    { name: 'author', content: authorName.value },
    { name: 'robots', content: 'index, follow' },
    { name: 'language', content: hreflangFor(pageLocale.value) },
    // Open Graph
    { property: 'og:title', content: seoTitleFinal },
    { property: 'og:description', content: seoDescriptionFinal },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(pageLocale.value) },
    { property: 'og:site_name', content: 'Veeso' },
    ...(ogImageUrl ? [{ property: 'og:image', content: ogImageUrl }] : []),
    ...(ogImageUrl
      ? [{ property: 'og:image:alt', content: art.ogImageAlt || art.imageAlt || art.title || '' }]
      : []),
    ...(ogImageUrl ? [{ property: 'og:image:width', content: '1200' }] : []),
    ...(ogImageUrl ? [{ property: 'og:image:height', content: '630' }] : []),
    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: seoTitleFinal },
    { name: 'twitter:description', content: seoDescriptionFinal },
    ...(ogImageUrl ? [{ name: 'twitter:image', content: ogImageUrl }] : []),
    ...(ogImageUrl
      ? [
          {
            name: 'twitter:image:alt',
            content: art.ogImageAlt || art.imageAlt || art.title || '',
          },
        ]
      : []),
    // Article meta
    ...(art.publishedAt ? [{ name: 'article:published_time', content: art.publishedAt }] : []),
    ...(art._updatedAt ? [{ name: 'article:modified_time', content: art._updatedAt }] : []),
    { name: 'article:author', content: authorName.value },
    ...(art.categories?.[0]?.title
      ? [{ name: 'article:section', content: art.categories[0].title }]
      : []),
    ...(art.tags && art.tags.length > 0
      ? art.tags.map((tag) => ({ name: 'article:tag', content: tag }))
      : []),
  ];

  // Schema.org Structured Data
  const structuredData: any[] = [];

  // Article Schema
  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: art.title,
    description: seoDescriptionFinal,
    inLanguage: hreflangFor(pageLocale.value),
    image: ogImageUrl
      ? {
          '@type': 'ImageObject',
          url: ogImageUrl,
          width: 1200,
          height: 630,
          alt: art.ogImageAlt || art.imageAlt || art.title || '',
        }
      : undefined,
    datePublished: art.publishedAt,
    dateModified: art._updatedAt || art.publishedAt,
    author: {
      '@type': 'Person',
      name: authorName.value,
      image: authorAvatar.value,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Veeso',
      logo: {
        '@type': 'ImageObject',
        url: 'https://veeso.ai/images/veesoai.png',
        width: 512,
        height: 512,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl.value,
    },
    ...(art.categories?.[0]?.title ? { articleSection: art.categories[0].title } : {}),
    ...(keywordsStrFinal ? { keywords: keywordsStrFinal } : {}),
  });

  // VideoObject — 嵌入 YouTube 时供 Google 识别为页面内视频（缓解 “Video isn't on a watch page”）
  const embeddedYoutubeVideos: Array<{ id: string; name: string; description: string }> = [];
  const youtubeSeenIds = new Set<string>();
  const addYoutubeForSchema = (
    url: string | undefined,
    title: string | undefined,
    caption: string | undefined
  ) => {
    if (!url) return;
    const id = extractYouTubeVideoId(url);
    if (!id || youtubeSeenIds.has(id)) return;
    youtubeSeenIds.add(id);
    embeddedYoutubeVideos.push({
      id,
      name: (title && String(title).trim()) || seoTitleFinal || art.title || 'Video',
      description: (caption && String(caption).trim()) || seoDescriptionFinal,
    });
  };
  for (const block of art.bodyContent || []) {
    const b = block as { type?: string; url?: string; title?: string; caption?: string };
    if (b?.type === 'youtube' && b.url) {
      addYoutubeForSchema(b.url, b.title, b.caption);
    }
  }
  for (const y of art.youtubeLinks || []) {
    if (y?.url) {
      addYoutubeForSchema(y.url, y.title, undefined);
    }
  }
  for (const v of embeddedYoutubeVideos) {
    structuredData.push({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: v.name,
      description: v.description,
      thumbnailUrl: `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`,
      uploadDate: art.publishedAt,
      embedUrl: `https://www.youtube.com/embed/${v.id}`,
      contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
      isPartOf: {
        '@type': 'WebPage',
        '@id': canonicalUrl.value,
      },
    });
  }

  // Breadcrumb Schema
  const breadcrumbItems: any[] = [
    {
      '@type': 'ListItem',
      position: 1,
      name: t('landingPages.articleDetail.breadcrumb.home'),
      item: 'https://veeso.ai/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: t('landingPages.spotlight.detail.breadcrumb.spotlight'),
      item: absoluteLocalizedUrl(pageLocale.value, '/spotlight'),
    },
  ];

  breadcrumbItems.push({
    '@type': 'ListItem',
    position: 3,
    name: art.title,
    item: canonicalUrl.value,
  });

  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  });

  // FAQ Schema (如果有 FAQ)
  if (faqItems.value.length > 0) {
    structuredData.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.value.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  useHead({
    title: seoTitleFinal || `${art.title} | Veeso Spotlight`,
    htmlAttrs: { lang: pageLocale.value },
    meta: metaTags,
    link: [
      { rel: 'canonical', href: canonicalUrl.value, key: 'canonical' },
      ...alternateHeadLinks.value,
    ],
    script: structuredData.map((schema) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema),
    })),
  });
}
</script>

<template>
  <div class="min-h-screen relative flex flex-col transition-colors duration-300 bg-[#f5f5f5]">
    <AppNav active-path="/spotlight" />

    <template v-if="article">
      <main class="relative z-[1] pt-[72px]">
        <div class="max-w-[1200px] mx-auto px-4 md:px-8 relative">
          <aside
            v-if="tableOfContents.length > 0"
            class="hidden xl:block fixed z-10"
            :style="{
              '--content-left': 'calc((100vw - 1200px) / 2)',
              /* TOC width +15% vs 400px max — more room before headings wrap */
              '--toc-left': 'max(1rem, calc(var(--content-left) - 460px))',
              '--min-gap': '0px',
              '--content-padding': '2rem',
              '--sidebar-offset': `${sidebarOffset}px`,
              left: 'var(--toc-left)',
              width:
                'clamp(230px, calc(var(--content-left) + var(--content-padding) - var(--toc-left) - var(--min-gap)), 460px)',
              top: `calc(50vh - ${sidebarOffset}px)`,
              transform: 'translateY(-50%)',
            }"
          >
            <nav class="w-full">
              <div class="text-sm font-semibold text-black/60 mb-12 uppercase tracking-widest">
                {{ t('landingPages.articleDetail.toc.title') }}
              </div>
              <ul class="space-y-6">
                <li v-for="item in tableOfContents" :key="item.id" class="relative">
                  <a
                    :href="`#${item.id}`"
                    @click.prevent="scrollToAnchor(item.id)"
                    class="text-sm transition-all duration-200 block leading-relaxed group relative pl-5"
                    :class="
                      activeTocId === item.id
                        ? 'text-black/90'
                        : 'text-black/40 hover:text-black/70'
                    "
                  >
                    <span
                      v-if="activeTocId === item.id"
                      class="absolute left-0 top-0 bottom-0 w-0.5 bg-black/60 rounded-full"
                    ></span>
                    <span
                      class="relative block group-hover:translate-x-1 transition-transform duration-200 whitespace-normal"
                      >{{ item.text }}</span
                    >
                  </a>
                </li>
              </ul>
            </nav>
          </aside>

          <article class="max-w-[800px] mx-auto py-12">
            <nav class="flex items-center gap-2 text-sm mb-6">
              <a href="/" class="text-black/60 hover:text-black/90 transition-colors">{{
                t('landingPages.articleDetail.breadcrumb.home')
              }}</a>
              <svg
                class="w-4 h-4 text-black/30"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <a
                :href="localizedPath(pageLocale, '/spotlight')"
                class="text-black/60 hover:text-black/90 transition-colors"
                >{{ t('landingPages.spotlight.detail.breadcrumb.spotlight') }}</a
              >
              <svg
                class="w-4 h-4 text-black/30"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <span class="text-black/90 font-medium">{{ article?.title }}</span>
            </nav>

            <header class="mb-12">
              <h1 class="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] text-black/90 mb-6">
                {{ article?.title }}
              </h1>

              <div class="flex items-center gap-4 mb-6">
                <img
                  :src="authorAvatar"
                  :alt="authorName"
                  class="w-12 h-12 rounded-full object-cover"
                />
                <div class="flex-1">
                  <div class="font-medium text-black/90">{{ authorName }}</div>
                </div>
              </div>

              <div class="flex items-center gap-4 pb-8 border-b border-black/10 flex-wrap">
                <span
                  v-if="article?.categories?.[0]?.title"
                  class="text-xs font-medium px-3 py-1.5 rounded-full bg-black/5 text-black/70"
                >
                  {{ article?.categories?.[0]?.title }}
                </span>
                <span v-if="article?.readTime" class="text-xs text-black/50">
                  {{ t('landingPages.articleDetail.meta.min_read', { count: article?.readTime }) }}
                </span>
                <span v-if="article?.publishedAt" class="text-xs text-black/50">
                  {{ t('landingPages.articleDetail.meta.last_updated') }}
                  {{ formatDate(article?.publishedAt) }}
                </span>
              </div>
            </header>

            <div
              v-if="article?.image"
              class="mb-12 rounded-lg overflow-hidden bg-black/5 aspect-video"
            >
              <img
                :src="article?.image"
                :alt="article?.imageAlt || article?.title"
                class="w-full h-full object-cover"
                loading="eager"
              />
            </div>

            <div
              v-if="
                !article?.excerptDerivedFromBody &&
                article?.excerpt &&
                ((Array.isArray(article.excerpt) && article.excerpt.length > 0) ||
                  (typeof article.excerpt === 'string' &&
                    (article.excerpt as string).trim().length > 0))
              "
              class="w-full prose prose-lg max-w-none mb-12 prose-headings:font-bold prose-p:leading-relaxed prose-p:font-normal prose-strong:font-bold prose-a:font-bold excerpt-links"
            >
              <SpotlightPortableBody
                v-if="Array.isArray(article.excerpt) && article.excerpt.length > 0"
                :value="article.excerpt"
              />
              <template v-else-if="typeof article.excerpt === 'string'">
                <template
                  v-for="(paragraph, index) in parseExcerptParagraphs(article.excerpt as string)"
                  :key="index"
                >
                  <p class="mb-6 leading-relaxed" v-html="paragraph"></p>
                </template>
              </template>
            </div>

            <div
              v-if="article?.bodyPortableText && article.bodyPortableText.length > 0"
              class="w-full"
            >
              <div
                class="prose prose-lg max-w-none mb-12 prose-headings:font-bold prose-p:leading-relaxed prose-strong:font-bold prose-a:font-bold prose-a:text-black prose-a:no-underline prose-a:border-b prose-a:border-black/15 prose-a:pb-[1px]"
              >
                <SpotlightPortableBody :value="article.bodyPortableText" />
              </div>
            </div>

            <div v-else-if="articleBodyContent.length > 0" class="w-full">
              <div
                class="prose prose-lg max-w-none mb-12 prose-headings:font-bold prose-p:leading-relaxed spotlight-body"
              >
                <template v-for="(block, index) in articleBodyContent" :key="index">
                  <div
                    v-if="block.type === 'paragraphHtml'"
                    class="mb-6 leading-relaxed spotlight-body"
                    v-html="(block as { html?: string }).html"
                  />

                  <p v-else-if="block.type === 'paragraph'" class="mb-6 leading-relaxed">
                    <template v-for="(part, partIndex) in renderParagraph(block)" :key="partIndex">
                      <span v-if="part.type === 'text'">{{ part.content }}</span>
                      <span v-else-if="part.type === 'bold'" class="font-bold">{{
                        part.content
                      }}</span>
                      <a
                        v-else-if="part.type === 'link'"
                        :href="part.url"
                        :target="part.target || '_blank'"
                        rel="nofollow noreferrer"
                        class="font-bold text-black no-underline border-b border-black/15 pb-[1px]"
                        >{{ part.content }}</a
                      >
                    </template>
                  </p>

                  <h2
                    v-else-if="block.type === 'heading' && block.level === 2"
                    :id="generateSlug(block.text)"
                    class="font-bold mt-12 mb-6 scroll-mt-24 text-[2rem] md:text-[2.5rem] leading-[1.1]"
                  >
                    {{ block.text }}
                  </h2>
                  <h3
                    v-else-if="block.type === 'heading' && block.level === 3"
                    :id="generateSlug(block.text)"
                    class="font-bold mt-10 mb-5 scroll-mt-24 text-2xl"
                  >
                    {{ block.text }}
                  </h3>
                  <h4
                    v-else-if="block.type === 'heading' && block.level === 4"
                    :id="generateSlug(block.text)"
                    class="font-bold mt-8 mb-4 scroll-mt-24 text-xl"
                  >
                    {{ block.text }}
                  </h4>
                  <h5
                    v-else-if="block.type === 'heading' && block.level === 5"
                    :id="generateSlug(block.text)"
                    class="font-bold mt-6 mb-3 scroll-mt-24 text-lg"
                  >
                    {{ block.text }}
                  </h5>
                  <h6
                    v-else-if="block.type === 'heading' && block.level === 6"
                    :id="generateSlug(block.text)"
                    class="font-bold mt-4 mb-2 scroll-mt-24 text-base"
                  >
                    {{ block.text }}
                  </h6>

                  <ul
                    v-else-if="block.type === 'list' && block.listType === 'unordered'"
                    class="not-prose spotlight-ul mb-6 space-y-2"
                  >
                    <li v-for="(item, itemIndex) in block.items" :key="itemIndex" class="mb-2">
                      <span v-if="(item as any).html" v-html="(item as any).html" />
                      <template
                        v-else
                        v-for="(part, partIndex) in renderListItem(item)"
                        :key="partIndex"
                      >
                        <span v-if="part.type === 'text'">{{ part.content }}</span>
                        <span v-else-if="part.type === 'bold'" class="font-bold">{{
                          part.content
                        }}</span>
                        <a
                          v-else-if="part.type === 'link'"
                          :href="part.url"
                          :target="part.target || '_blank'"
                          rel="nofollow noreferrer"
                          class="font-bold text-black no-underline border-b border-black/15 pb-[1px]"
                          >{{ part.content }}</a
                        >
                      </template>
                    </li>
                  </ul>
                  <ol
                    v-else-if="block.type === 'list' && block.listType === 'ordered'"
                    class="not-prose spotlight-ol mb-6 space-y-2"
                  >
                    <li v-for="(item, itemIndex) in block.items" :key="itemIndex" class="mb-2">
                      <span v-if="(item as any).html" v-html="(item as any).html" />
                      <template
                        v-else
                        v-for="(part, partIndex) in renderListItem(item)"
                        :key="partIndex"
                      >
                        <span v-if="part.type === 'text'">{{ part.content }}</span>
                        <span v-else-if="part.type === 'bold'" class="font-bold">{{
                          part.content
                        }}</span>
                        <a
                          v-else-if="part.type === 'link'"
                          :href="part.url"
                          :target="part.target || '_blank'"
                          rel="nofollow noreferrer"
                          class="font-bold text-black no-underline border-b border-black/15 pb-[1px]"
                          >{{ part.content }}</a
                        >
                      </template>
                    </li>
                  </ol>

                  <figure v-else-if="block.type === 'image'" class="w-full my-8">
                    <div
                      v-if="
                        block.src && !block.src.includes('.mp4') && !block.src.includes('/video/')
                      "
                      class="relative w-full rounded-lg overflow-hidden bg-black/5"
                    >
                      <img
                        :src="block.src"
                        :alt="
                          block.alt ||
                          article?.title ||
                          t('landingPages.spotlight.detail.image_alt_fallback')
                        "
                        class="w-full h-auto block"
                        loading="lazy"
                      />
                    </div>
                    <figcaption
                      v-if="block.captionHtml"
                      class="spotlight-media-caption mt-2 text-sm text-center text-black/50"
                      v-html="block.captionHtml"
                    />
                  </figure>
                  <div v-else-if="block.type === 'youtube'" class="w-full my-8">
                    <div
                      v-if="extractYouTubeVideoId(block.url)"
                      class="relative w-full rounded-lg overflow-hidden bg-black/5"
                      style="aspect-ratio: 16/9"
                    >
                      <iframe
                        :src="`https://www.youtube.com/embed/${extractYouTubeVideoId(block.url)}`"
                        :title="
                          block.title || t('landingPages.spotlight.detail.video_title_fallback')
                        "
                        class="absolute top-0 left-0 w-full h-full"
                        frameborder="0"
                        allow="
                          accelerometer;
                          autoplay;
                          clipboard-write;
                          encrypted-media;
                          gyroscope;
                          picture-in-picture;
                          web-share;
                        "
                        allowfullscreen
                        loading="lazy"
                      ></iframe>
                    </div>
                    <p v-if="block.caption" class="mt-2 text-sm text-black/50 text-center">
                      {{ block.caption }}
                    </p>
                    <p v-else-if="block.title" class="mt-2 text-sm text-black/50 text-center">
                      {{ block.title }}
                    </p>
                  </div>
                </template>
              </div>
            </div>

            <div
              v-if="article?.youtubeLinks && article.youtubeLinks.length > 0"
              class="w-full mb-12 space-y-8"
            >
              <div v-for="(youtubeLink, index) in article.youtubeLinks" :key="index" class="w-full">
                <div
                  v-if="extractYouTubeVideoId(youtubeLink.url)"
                  class="relative w-full rounded-lg overflow-hidden bg-black/5"
                  style="aspect-ratio: 16/9"
                >
                  <iframe
                    :src="`https://www.youtube.com/embed/${extractYouTubeVideoId(youtubeLink.url)}`"
                    :title="
                      youtubeLink.title || t('landingPages.spotlight.detail.video_title_fallback')
                    "
                    class="absolute top-0 left-0 w-full h-full"
                    frameborder="0"
                    allow="
                      accelerometer;
                      autoplay;
                      clipboard-write;
                      encrypted-media;
                      gyroscope;
                      picture-in-picture;
                      web-share;
                    "
                    allowfullscreen
                    loading="lazy"
                  ></iframe>
                </div>
                <p v-if="youtubeLink.title" class="mt-2 text-sm text-black/50 text-center">
                  {{ youtubeLink.title }}
                </p>
              </div>
            </div>
          </article>

          <section
            v-if="faqItems.length > 0"
            id="faq-section"
            class="max-w-xl mx-auto mb-24 pt-12 scroll-mt-24"
          >
            <div class="text-center mb-16">
              <h2 class="text-[2rem] md:text-[2.5rem] font-bold leading-[1.1] text-black/90 mb-4">
                {{ faqSectionTitle }}
              </h2>
            </div>

            <div class="max-w-xl mx-auto">
              <div>
                <div
                  v-for="(item, index) in faqItems"
                  :key="index"
                  class="transition-all duration-300"
                  :class="[
                    index !== faqItems.length - 1 ? 'border-b border-black/5 pb-4 mb-4' : '',
                  ]"
                >
                  <button
                    type="button"
                    @click="toggleFaqItem(index)"
                    class="flex flex-1 items-center justify-between w-full py-4 text-left font-bold transition-all hover:underline text-black/90"
                  >
                    <h3 class="faq-question-title text-xl font-bold pr-4">{{ item.question }}</h3>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      class="h-5 w-5 shrink-0 text-black/40 transition-transform duration-200"
                      :class="[isFaqExpanded(index) ? 'rotate-90' : '']"
                    >
                      <path
                        fill="currentColor"
                        d="M9.47 6.47a.75.75 0 0 1 1.06 0l5 5a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 1 1-1.06-1.06L13.94 12 9.47 7.53a.75.75 0 0 1 0-1.06"
                      ></path>
                    </svg>
                  </button>
                  <div
                    v-show="isFaqExpanded(index)"
                    class="pb-4 transition-all duration-200 ease-in-out"
                  >
                    <div
                      class="faq-answer text-base text-black/70 font-light"
                      v-html="item.answerHtml"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <aside
            v-if="shareLinks"
            class="hidden xl:block fixed z-10 right-4 transition-transform duration-300"
            :style="{
              top: `calc(50vh - ${sidebarOffset}px)`,
              transform: 'translateY(-50%)',
            }"
          >
            <div class="flex flex-col items-end gap-2.5 relative group">
              <button
                @click="handleShare('twitter')"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-transparent hover:bg-black/10 transition-all duration-200 group/btn relative"
                :aria-label="t('landingPages.articleDetail.share.aria_twitter')"
              >
                <svg
                  class="w-4 h-4 text-black/40 group-hover/btn:text-black/70 transition-all group-hover/btn:scale-110"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                  />
                </svg>
                <span
                  class="absolute right-full mr-3 px-2 py-1 text-xs text-white bg-black/80 rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none"
                >
                  {{ t('landingPages.articleDetail.share.twitter') }}
                </span>
              </button>

              <button
                @click="handleShare('linkedin')"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-transparent hover:bg-black/10 transition-all duration-200 group/btn relative"
                :aria-label="t('landingPages.articleDetail.share.aria_linkedin')"
              >
                <svg
                  class="w-4 h-4 text-black/40 group-hover/btn:text-black/70 transition-all group-hover/btn:scale-110"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                  />
                </svg>
                <span
                  class="absolute right-full mr-3 px-2 py-1 text-xs text-white bg-black/80 rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none"
                >
                  {{ t('landingPages.articleDetail.share.linkedin') }}
                </span>
              </button>

              <button
                @click="handleShare('discord')"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-transparent hover:bg-black/10 transition-all duration-200 group/btn relative"
                :aria-label="t('landingPages.articleDetail.share.aria_discord')"
              >
                <svg
                  class="w-4 h-4 text-black/40 group-hover/btn:text-black/70 transition-all group-hover/btn:scale-110"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"
                  />
                </svg>
                <span
                  class="absolute right-full mr-3 px-2 py-1 text-xs text-white bg-black/80 rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none"
                >
                  {{ t('landingPages.articleDetail.share.discord') }}
                </span>
              </button>

              <button
                @click="handleShare('facebook')"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-transparent hover:bg-black/10 transition-all duration-200 group/btn relative"
                :aria-label="t('landingPages.articleDetail.share.aria_facebook')"
              >
                <svg
                  class="w-4 h-4 text-black/40 group-hover/btn:text-black/70 transition-all group-hover/btn:scale-110"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
                <span
                  class="absolute right-full mr-3 px-2 py-1 text-xs text-white bg-black/80 rounded whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none"
                >
                  {{ t('landingPages.articleDetail.share.facebook') }}
                </span>
              </button>
            </div>
          </aside>

          <div
            v-if="recommendedPosts.length >= 4"
            class="w-full border-t border-black/10 mb-12"
            data-divider="recommended"
          ></div>
        </div>

        <section
          v-if="recommendedPosts.length >= 4"
          class="max-w-[1200px] mx-auto px-4 md:px-8 pb-24 overflow-hidden"
          :aria-label="t('ui.a11y.recommendedPosts')"
        >
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-2xl md:text-3xl font-bold text-black/90">
              {{ t('landingPages.articleDetail.recommended.title') }}
            </h2>
            <NuxtLink
              to="/spotlight"
              class="text-sm font-medium text-black/60 hover:text-black/90 transition-colors"
            >
              {{ t('landingPages.articleDetail.recommended.see_all') }}
            </NuxtLink>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
            <NuxtLink
              v-for="recommendedPost in recommendedPosts"
              :key="recommendedPost.id"
              :to="localizedPath(pageLocale, `/spotlight/${recommendedPost.slug}`)"
              class="cursor-pointer flex flex-col bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02] shadow-[0_3px_7px_0px_rgba(0,0,0,0.05)] group w-full"
            >
              <div class="h-48 relative overflow-hidden bg-black/5">
                <img
                  :src="recommendedPost.image"
                  :alt="recommendedPost.title"
                  class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div class="flex-1 flex flex-col p-6">
                <span
                  class="text-xs font-medium px-3 py-1.5 rounded-full bg-black/5 text-black/70 mb-3 w-fit"
                >
                  {{ recommendedPost.category }}
                </span>
                <h3 class="text-lg font-bold text-black/90 mb-2 line-clamp-2">
                  {{ recommendedPost.title }}
                </h3>
              </div>
            </NuxtLink>
          </div>
        </section>

        <div data-section="cta" :aria-label="t('ui.a11y.ctaSection')">
          <FeaturesRoles />
        </div>
      </main>
    </template>

    <AppFooter v-if="article" />
  </div>
</template>

<style scoped>
/* Styles are handled via Tailwind CSS classes */

/* Article body & excerpt link styles — applied via :deep() so v-html content is targeted */
/* Body / excerpt: Sanity strong marks should render as real bold text. */
.spotlight-body :deep(strong),
.excerpt-links :deep(strong) {
  font-weight: 700;
}

.spotlight-body :deep(h2),
.spotlight-body :deep(h3),
.spotlight-body :deep(h4),
.spotlight-body :deep(h5),
.spotlight-body :deep(h6),
.spotlight-body :deep(h2 *),
.spotlight-body :deep(h3 *),
.spotlight-body :deep(h4 *),
.spotlight-body :deep(h5 *),
.spotlight-body :deep(h6 *),
.excerpt-links :deep(h2),
.excerpt-links :deep(h3),
.excerpt-links :deep(h4),
.excerpt-links :deep(h5),
.excerpt-links :deep(h6),
.excerpt-links :deep(h2 *),
.excerpt-links :deep(h3 *),
.excerpt-links :deep(h4 *),
.excerpt-links :deep(h5 *),
.excerpt-links :deep(h6 *) {
  font-weight: 700 !important;
}

.spotlight-body :deep(a),
.excerpt-links :deep(a) {
  font-weight: 700;
  color: rgb(0, 0, 0);
  text-decoration: underline;
  text-decoration-color: rgba(0, 0, 0, 0.35);
  text-underline-offset: 3px;
  transition: text-decoration-color 0.15s ease;
}

.spotlight-body :deep(a:hover),
.excerpt-links :deep(a:hover) {
  text-decoration-color: rgba(0, 0, 0, 0.85);
}

/* Portable Text: catch all anchors (not only .spotlight-pt-link) under prose */
.excerpt-links :deep(.spotlight-pt-root a[href]),
.prose :deep(.spotlight-pt-root a[href]) {
  font-weight: 700 !important;
  color: rgb(0, 0, 0) !important;
  text-decoration: none !important;
  border-bottom: 1px solid rgb(0 0 0 / 0.15);
  padding-bottom: 1px;
}
.excerpt-links :deep(.spotlight-pt-root a[href] *),
.prose :deep(.spotlight-pt-root a[href] *) {
  font-weight: inherit !important;
}
.excerpt-links :deep(.spotlight-pt-root a[href] span),
.prose :deep(.spotlight-pt-root a[href] span) {
  font-weight: 700 !important;
}
.excerpt-links :deep(.spotlight-pt-root a[href] strong),
.prose :deep(.spotlight-pt-root a[href] strong) {
  font-weight: 700 !important;
}

/* Inline / paragraph images — same rounded container feel as YouTube embeds */
.spotlight-body :deep(img),
.excerpt-links :deep(img) {
  border-radius: 0.5rem;
}

/* Image / embed captions — match YouTube figcaption: centered, muted */
.spotlight-body .spotlight-media-caption :deep(a) {
  font-weight: 700;
  color: rgb(0 0 0 / 0.9);
  text-decoration: none;
  border-bottom: 1px solid rgb(0 0 0 / 0.15);
  padding-bottom: 1px;
}

.spotlight-body .spotlight-media-caption :deep(a:hover) {
  border-bottom-color: rgb(0 0 0 / 0.85);
}

/*
 * Lists: keep the dot in the first text line's vertical center.
 * Use block + absolute marker; flex/grid split inline content too easily here.
 */
.spotlight-ul {
  list-style: none;
  padding-left: 0;
  margin: 0 0 1.75rem;
  margin-left: 1.25rem;
  font-size: inherit;
  line-height: 1.75;
}

@media (min-width: 768px) {
  .spotlight-ul {
    margin-left: 1.75rem;
  }
}

.spotlight-ul > li {
  position: relative;
  display: block;
  margin-bottom: 0.45rem;
  padding-left: 1rem;
}

.spotlight-ul > li::before {
  content: '·';
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  width: 0.5rem;
  height: 1.75em;
  align-items: center;
  justify-content: center;
  font-size: 1em;
  line-height: 1;
  font-weight: 700;
  color: rgb(0 0 0 / 0.4);
  transform: scale(1.25);
  transform-origin: center;
}

.spotlight-ol {
  list-style: none;
  counter-reset: spotlight-number;
  margin: 0 0 1.75rem;
  margin-left: 1.25rem;
  padding-left: 0;
  font-size: inherit;
  line-height: 1.75;
}

@media (min-width: 768px) {
  .spotlight-ol {
    margin-left: 1.75rem;
  }
}

.spotlight-ol > li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.45rem;
  counter-increment: spotlight-number;
}

.spotlight-ol > li::before {
  content: counter(spotlight-number) '、';
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  height: 1.75em;
  align-items: center;
  justify-content: flex-start;
  font-size: 1em;
  line-height: 1;
  font-weight: 600;
  color: rgb(0 0 0 / 0.55);
}

.faq-answer :deep(p) {
  margin: 0 0 0.9rem;
  line-height: 1.75;
}

.faq-answer :deep(p:last-child) {
  margin-bottom: 0;
}

.faq-question-title,
.faq-answer :deep(h2),
.faq-answer :deep(h3),
.faq-answer :deep(h4),
.faq-answer :deep(h5),
.faq-answer :deep(h6),
.faq-answer :deep(h2 *),
.faq-answer :deep(h3 *),
.faq-answer :deep(h4 *),
.faq-answer :deep(h5 *),
.faq-answer :deep(h6 *),
.faq-answer :deep(strong) {
  font-weight: 700 !important;
}

.faq-answer :deep(ul),
.faq-answer :deep(ol) {
  margin: 0.75rem 0 0.9rem 1.25rem;
  padding-left: 1rem;
}

.faq-answer :deep(li) {
  margin-bottom: 0.45rem;
}

.faq-answer :deep(a) {
  font-weight: 600;
  color: rgb(0, 0, 0);
  text-decoration: underline;
  text-decoration-color: rgba(0, 0, 0, 0.35);
  text-underline-offset: 3px;
}
</style>
